import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect browser language
const detectLanguage = () => {
  const browserLang = navigator.language.split('-')[0];
  const supportedLangs = ['sq', 'de', 'en'];
  if (supportedLangs.includes(browserLang)) return browserLang;
  return 'en';
};

// Get saved preferences
const getSavedTheme = () => localStorage.getItem('theme') || 'dark';
const getSavedLanguage = () => localStorage.getItem('language') || detectLanguage();

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [theme, setTheme] = useState(getSavedTheme);
  const [language, setLanguage] = useState(getSavedLanguage);
  const [translations, setTranslations] = useState({});
  const [settings, setSettings] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Save language preference
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Fetch translations
  const fetchTranslations = useCallback(async (lang) => {
    try {
      const response = await axios.get(`${API}/public/translations/${lang}`);
      setTranslations(response.data.translations || {});
    } catch (error) {
      console.error('Failed to fetch translations:', error);
    }
  }, []);

  // Fetch public settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/settings`);
      setSettings(response.data);
      
      // Apply theme from settings if not set by user
      if (!localStorage.getItem('theme') && response.data?.theme_settings?.default_theme) {
        setTheme(response.data.theme_settings.default_theme);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  // Check auth status
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/me`, { withCredentials: true });
      setUser(response.data.user);
    } catch (error) {
      setUser(null);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      await Promise.all([
        fetchSettings(),
        fetchTranslations(language),
        checkAuth()
      ]);
      setLoading(false);
    };
    init();
  }, [fetchSettings, fetchTranslations, checkAuth, language]);

  // Update translations when language changes
  useEffect(() => {
    fetchTranslations(language);
  }, [language, fetchTranslations]);

  // Translation helper
  const t = (key, fallback = key) => {
    return translations[key] || fallback;
  };

  // Toggle theme
  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  // Login
  const login = async (username, password) => {
    const response = await axios.post(`${API}/admin/login`, { username, password }, { withCredentials: true });
    setUser(response.data.user);
    return response.data;
  };

  // Logout
  const logout = async () => {
    await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    language,
    setLanguage,
    translations,
    t,
    settings,
    fetchSettings,
    user,
    setUser,
    login,
    logout,
    checkAuth,
    loading,
    API
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
